import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Input,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Tabs,
  Button,
  Space,
  Avatar,
} from "antd";
import {
  SearchOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  AuditOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  UserOutlined,
  ClockCircleOutlined,
  FlagOutlined,
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import CorporateReportingPage from "./CorporateReporting.styles";
import { corporateReportingService } from "@iso/services/corporateReporting.service";
import { TYPES, STATUSES, INVESTIGATION_TYPES, findIncidentTypeOption } from '@iso/constants/incidentOptions';
import {
  hazardStatus,
  hazardEscalationLevels,
  getStatusColor,
  getEscalationLevelShortLabel,
  HAZARD_CLOSED_STATUSES
} from '@iso/constants/hazardStatus';
import { getSafetyActivityHazardLabel } from '@iso/constants/hazardTypes';
import DeadlineCountdown from '@iso/components/Hazards/DeadlineCountdown';
import { commonHelper } from '@iso/lib/helpers/commonHelper';
import moment from "moment";

import useIncidentActions from "../Incident/shared/useIncidentActions";
import IncidentActionModals from "../Incident/shared/IncidentActionModals";
import { getActionColumn, getApprovalColumn } from "../Incident/shared/incidentActionColumns";

import useHazardActions from "../HazardManagement/shared/useHazardActions";
import HazardActionModals from "../HazardManagement/shared/HazardActionModals";
import { getHazardActionColumn } from "../HazardManagement/shared/hazardActionColumns";

const { TabPane } = Tabs;

const statusColors = {
  submitted_pending_safety_review: 'orange',
  safety_review_completed: 'blue',
  investigation_in_progress: 'processing',
  investigation_submitted_to_safety: 'gold',
  investigation_approved: 'cyan',
  lessons_learned_pending_review: 'purple',
  distributed: 'geekblue',
  closed: 'green',
};

const IncidentsTab = () => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const currentUserId = useSelector((state) => state.user?.user?.id);

  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    totalCount: 0,
  });
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const stats = {
    totalOpen: incidents.filter(i => i.status !== 'closed').length,
    underInvestigation: incidents.filter(i => i.status === 'investigation_in_progress').length,
    closed: incidents.filter(i => i.status === 'closed').length,
  };

  const fetchIncidents = useCallback(async () => {
    if (!activePropertyId) return;
    setLoading(true);

    try {
      const response = await corporateReportingService.getIncidents({
        property_id: activePropertyId,
        page: pagination.currentPage,
        per_page: pagination.perPage,
        search: debouncedSearchInput,
        type: selectedType,
        status: selectedStatus,
      });

      setIncidents(response.items);
      setPagination(prev => ({ ...prev, ...response._meta }));
    } catch (error) {
      console.error('Failed to fetch corporate incidents:', error);
    } finally {
      setLoading(false);
    }
  }, [activePropertyId, pagination.currentPage, pagination.perPage, debouncedSearchInput, selectedType, selectedStatus]);

  const actions = useIncidentActions({ onRefresh: fetchIncidents });

  // group_safety_manager has full access in corporate reporting
  const isSafetyAdmin = true;
  const canManageIncidents = true;
  const canDecideApproval = true;

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchInput(searchInput);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchInput]);

  const handleTableChange = (paginationConfig, filters) => {
    setSelectedType(filters.type?.[0] ?? null);
    setSelectedStatus(filters.status?.[0] ?? null);
    setPagination(prev => ({
      ...prev,
      currentPage: paginationConfig.current,
      perPage: paginationConfig.pageSize,
    }));
  };

  const columns = [
    {
      title: 'Report #',
      dataIndex: 'report_number',
      key: 'report_number',
      sorter: (a, b) => (a.report_number || '').localeCompare(b.report_number || ''),
      render: (text) => <strong>{text}</strong>,
      width: 150,
    },
    {
      title: 'Property',
      dataIndex: 'property_name',
      key: 'property_name',
      render: (text) => text || '-',
      width: 150,
    },
    {
      title: 'Date',
      dataIndex: 'date_time',
      key: 'date_time',
      sorter: (a, b) => moment(a.date_time).unix() - moment(b.date_time).unix(),
      render: (text) => moment(text).format("DD/MM/YYYY HH:mm"),
      width: 150,
    },
    {
      title: 'Incident Type',
      dataIndex: 'type',
      key: 'type',
      filters: TYPES.map(t => ({ text: t.label, value: t.value })),
      filteredValue: selectedType ? [selectedType] : null,
      filterMultiple: false,
      render: (text) => {
        const typeOption = findIncidentTypeOption(text);
        return typeOption ? typeOption.label : text;
      },
      width: 150,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: STATUSES.map(s => ({ text: s.label, value: s.value })),
      filteredValue: selectedStatus ? [selectedStatus] : null,
      filterMultiple: false,
      render: (text, record) => (
        <Tag color={statusColors[text] || 'default'}>
          {record.status_label || text}
        </Tag>
      ),
      width: 180,
    },
    {
      title: 'Investigation',
      dataIndex: 'investigation_type',
      key: 'investigation_type',
      render: (text) => {
        if (!text) return '-';
        const invType = INVESTIGATION_TYPES.find((t) => t.value === text);
        return invType ? invType.label : text;
      },
      width: 130,
    },
    getApprovalColumn({
      canDecideApproval,
      onApprovalOpen: actions.handleApprovalOpen,
    }),
    getActionColumn({
      isSafetyAdmin,
      canManageIncidents,
      currentUserId,
      handlers: actions,
    }),
  ];

  return (
    <>
      <div className="card-actions" style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by report number..."
          className="search-input"
          suffix={!searchInput && <SearchOutlined className="site-form-item-icon" />}
          prefix={searchInput && (
            <CloseCircleOutlined
              onClick={() => setSearchInput("")}
              className="site-form-item-cancel"
            />
          )}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ width: 250, height: 40 }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchIncidents}
          loading={loading}
          className="refresh-button"
          style={{ height: 40, borderRadius: 8 }}
        >
          Refresh
        </Button>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card className="stat-card">
            <Statistic
              title="Total Open"
              value={stats.totalOpen}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card">
            <Statistic
              title="Under Investigation"
              value={stats.underInvestigation}
              prefix={<AuditOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card">
            <Statistic
              title="Closed"
              value={stats.closed}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>
        Showing {incidents.length} of {pagination.totalCount} incidents
      </div>

      <Table
        columns={columns}
        dataSource={incidents}
        loading={loading}
        rowKey="id"
        pagination={{
          current: pagination.currentPage,
          pageSize: pagination.perPage,
          total: pagination.totalCount,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total) => `Total ${total} incidents`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1500 }}
      />

      <IncidentActionModals
        {...actions}
        isSafetyAdmin={isSafetyAdmin}
        currentUserId={currentUserId}
        canDecideApproval={canDecideApproval}
        onRefresh={fetchIncidents}
        onApprovalUpdated={(incidentId, approval) => {
          setIncidents((prev) =>
            prev.map((incident) =>
              incident.id === incidentId
                ? { ...incident, latest_approval: approval }
                : incident
            )
          );
        }}
      />
    </>
  );
};

const HazardsTab = () => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);

  const [hazards, setHazards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const fetchHazards = useCallback(async () => {
    if (!activePropertyId) return;
    setLoading(true);

    try {
      const result = await corporateReportingService.getHazards(activePropertyId);
      if (result && result.hazards) {
        setHazards(result.hazards);
      }
    } catch (error) {
      console.error('Failed to fetch corporate hazards:', error);
    } finally {
      setLoading(false);
    }
  }, [activePropertyId]);

  const hazardActions = useHazardActions({ onRefresh: fetchHazards });

  useEffect(() => {
    fetchHazards();
  }, [fetchHazards]);

  const stats = {
    totalOpen: hazards.filter(h => h.status === 'open' || h.status === 'in_progress').length,
    overdue: hazards.filter(h => h.workflow_state?.is_overdue && !HAZARD_CLOSED_STATUSES.includes(h.status)).length,
    level3: hazards.filter(h => h.current_escalation_level === 3 && (h.status === 'open' || h.status === 'in_progress')).length,
  };

  const searchFilteredHazards = hazards.filter(hazard => {
    if (searchText) {
      const displayLabel = getSafetyActivityHazardLabel(hazard.hazard_type) || hazard.title;
      if (!displayLabel.toLowerCase().includes(searchText.toLowerCase())) {
        return false;
      }
    }
    return true;
  });

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => {
        const displayLabel = getSafetyActivityHazardLabel(record.hazard_type) || text;
        return <Link to={`/hazards/${record.id}`} style={{ color: '#000000d9' }}><strong>{displayLabel}</strong></Link>;
      },
      width: 250,
    },
    {
      title: 'Property',
      dataIndex: 'property_name',
      key: 'property_name',
      render: (text) => text || '-',
      width: 150,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.replace(/_/g, ' ').toUpperCase()}
        </Tag>
      ),
      filters: hazardStatus.map(s => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => record.status === value,
      filterMultiple: true,
      width: 100,
    },
    {
      title: 'Level',
      dataIndex: 'current_escalation_level',
      key: 'level',
      render: (level) => (
        <Tag color={level === 3 ? 'red' : level === 2 ? 'orange' : 'blue'}>
          {getEscalationLevelShortLabel(level)}
        </Tag>
      ),
      filters: hazardEscalationLevels.map(l => ({ text: l.label, value: l.value })),
      onFilter: (value, record) => record.current_escalation_level === value,
      filterMultiple: true,
      width: 100,
    },
    {
      title: 'Assigned To',
      key: 'assigned',
      render: (_, record) => {
        const assignee = record.workflow_state?.current_assignee;
        if (!assignee) return '-';
        return (
          <Space size="small">
            <Avatar size="small" icon={<UserOutlined />} />
            <span>{assignee.name}</span>
          </Space>
        );
      },
      width: 180,
    },
    {
      title: 'Deadline',
      dataIndex: 'deadline',
      key: 'deadline',
      render: (deadline, record) => {
        if (!deadline) return '-';
        const isClosed = HAZARD_CLOSED_STATUSES.includes(record.status);
        return (
          <Space direction="vertical" size="small">
            <div style={{ fontSize: 12 }}>{commonHelper.displayDateTimeLocal(deadline, 'DD/MM/YY HH:mm')}</div>
            {!isClosed && <DeadlineCountdown deadline={deadline} compact />}
          </Space>
        );
      },
      width: 150,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => commonHelper.displayDateLocal(date),
      width: 100,
    },
    getHazardActionColumn({
      canEdit: true,
      showViewLink: true,
      handlers: hazardActions,
    }),
  ];

  return (
    <>
      <div className="card-actions" style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search..."
          className="search-input"
          suffix={!searchText && <SearchOutlined className="site-form-item-icon" />}
          prefix={searchText && (
            <CloseCircleOutlined
              onClick={() => setSearchText('')}
              className="site-form-item-cancel"
            />
          )}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 250, height: 40 }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={fetchHazards}
          loading={loading}
          className="refresh-button"
          style={{ height: 40, borderRadius: 8 }}
        >
          Refresh
        </Button>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card className="stat-card">
            <Statistic
              title="Total Open Hazards"
              value={stats.totalOpen}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card">
            <Statistic
              title="Overdue Hazards"
              value={stats.overdue}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#f5222d' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card" style={{ borderColor: '#f5222d', borderWidth: 2 }}>
            <Statistic
              title="Level 3 (URGENT)"
              value={stats.level3}
              prefix={<FlagOutlined />}
              valueStyle={{ color: '#f5222d', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
      </Row>

      <div style={{ marginBottom: 16, fontSize: 13, color: '#666' }}>
        Showing {searchFilteredHazards.length} of {hazards.length} hazards
      </div>

      <Table
        columns={columns}
        dataSource={searchFilteredHazards}
        loading={loading}
        rowKey="id"
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} hazards`,
        }}
        scroll={{ x: 1300 }}
      />

      <HazardActionModals
        {...hazardActions}
        propertyId={activePropertyId}
        onRefresh={fetchHazards}
      />
    </>
  );
};

const CorporateReporting = () => {
  return (
    <LayoutWrapper>
      <CorporateReportingPage>
        <Box id="containerBox">
          <div className="manage-property-header">
            <h2 className="manage-property-title" style={{ margin: 0 }}>
              Corporate Reporting
            </h2>
          </div>

          <Card className="holiday-card">
            <Tabs defaultActiveKey="incidents">
              <TabPane tab="Incidents" key="incidents">
                <IncidentsTab />
              </TabPane>
              <TabPane tab="Hazards" key="hazards">
                <HazardsTab />
              </TabPane>
            </Tabs>
          </Card>
        </Box>
      </CorporateReportingPage>
    </LayoutWrapper>
  );
};

export default CorporateReporting;
